import cn from 'classnames';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import type { Animal } from '@/model/Animal.model';
import type { ISODateTime } from '@/model/Common.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import InVivoError from '@/model/InVivoError';
import type { Observation } from '@/model/Observation.model';
import type { Study } from '@/model/Study.model';

import { errorToast, successToast } from '@/helpers';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import Loading from '@/components/Loading';
import ObservationsList from '@/components/Subjects/Show/Views/ObservationsList';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import SearchSelect from '@/components/UI/SearchSelect';
import { toWorkflowDateTime } from '@/components/Workflow/Show/Workflow.utils';

import type { WorkflowTabConfig } from '../Workflow.model';
import { WorkflowNoDataCard } from './WorkflowNoDataCard';

interface AddObservationModalProps {
  animalId: Animal['id'];
  closeModal: () => void;
  handleCallback: () => void;
  observed_at: ISODateTime;
  selectedObservations: Array<TeamGlossaryV1>;
}

type ObservationSubject = Animal & Required<Pick<Animal, 'api_id'>>;

interface ObservationViewProps {
  canWrite: boolean;
  fetchAnimalDataByType: (subject: ObservationSubject, type: 'observations') => void;
  observations: Array<Observation>;
  observed_at: ISODateTime;
  onSave: () => void;
  openModal: (modal: 'ADD_OBSERVATION', props: AddObservationModalProps) => void;
  study: Study;
  subject: ObservationSubject;
  workflow: Pick<WorkflowTabConfig, 'onSave' | 'preSelectedObservationTypeIds'>;
}

/**
 * Displays observation recording controls and the subject's observation history
 * within the workflow.
 */
const ObservationView: FC<ObservationViewProps> = ({
  study,
  subject,
  observations,
  fetchAnimalDataByType,
  observed_at,
  workflow,
  onSave,
  openModal,
  canWrite,
}) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<Array<string>>(workflow.preSelectedObservationTypeIds ?? []);
  const [isAddObservationModalOpen, setIsAddObservationModalOpen] = useState(false);
  const recordingNormalRef = useRef(false);

  const { response: observationsResponse, loading } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'observations',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: { onError: { toast: false, slug: 'workflow-observations-load' } },
  });
  const observationTypes = useMemo<Array<TeamGlossaryV1>>(
    () => (observationsResponse?.type === ResponseType.Success ? observationsResponse.body.data : []),
    [observationsResponse]
  );
  const observationsError = observationsResponse?.type === ResponseType.Error ? observationsResponse.error : undefined;

  const { invoke: recordNormal } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/animals/{animalApiId}/observations/normal',
    invokeOnInit: false,
    path: { studyApiId: study.api_id, animalApiId: subject.api_id },
    options: { onError: { throw: true, capture: false, toast: false, slug: 'record-normal-observation' } },
  });

  const observationTypeSections = useMemo(() => {
    const observationTypeMap = new Map<string, TeamGlossaryV1>(
      observationTypes.map((observationType) => [observationType.api_id, observationType])
    );

    const selectedTypes = selected.reduce<Array<TeamGlossaryV1>>((acc, observationTypeId) => {
      const observationType = observationTypeMap.get(observationTypeId);
      if (_notNil(observationType)) {
        acc.push(observationType);
        observationTypeMap.delete(observationTypeId);
      }
      return acc;
    }, []);
    return [
      ...(_isNotEmpty(selectedTypes) ? [{ title: 'Selected Observation types', items: selectedTypes }] : []),
      { title: 'Observation types', items: Array.from(observationTypeMap.values()) },
    ];
  }, [selected, observationTypes]);

  const closeAddObservationModal = useCallback(() => {
    setIsAddObservationModalOpen(false);
    dispatch({ type: 'CLOSE_MODAL' });
  }, [dispatch]);

  const handleCallback = useCallback(() => {
    onSave();
    closeAddObservationModal();
    setSelected([]);
    if (workflow.onSave !== 'next-subject') {
      fetchAnimalDataByType(subject, 'observations');
    }
  }, [onSave, closeAddObservationModal, workflow.onSave, fetchAnimalDataByType, subject]);

  const handleModalToggle = useCallback(() => {
    const observationTypeMap = new Map<string, TeamGlossaryV1>(
      observationTypes.map((observationType) => [observationType.api_id, observationType])
    );

    const selectedObservations = selected.reduce<Array<TeamGlossaryV1>>((acc, observationTypeId) => {
      const observationType = observationTypeMap.get(observationTypeId);
      if (_notNil(observationType)) {
        acc.push(observationType);
      }
      return acc;
    }, []);

    setIsAddObservationModalOpen(true);
    return openModal('ADD_OBSERVATION', {
      selectedObservations,
      animalId: subject.id,
      closeModal: closeAddObservationModal,
      observed_at: toWorkflowDateTime(observed_at),
      handleCallback,
    });
  }, [observationTypes, selected, openModal, subject.id, closeAddObservationModal, observed_at, handleCallback]);

  const handleRecordNormalObservation = useCallback(async () => {
    if (recordingNormalRef.current) {
      return;
    }

    recordingNormalRef.current = true;
    try {
      await recordNormal({
        body: {
          observed_at: toWorkflowDateTime(observed_at),
        },
        path: { studyApiId: study.api_id, animalApiId: subject.api_id },
      });
      successToast('Observation saved');
      onSave();
      setSelected([]);

      if (workflow.onSave !== 'next-subject') {
        fetchAnimalDataByType(subject, 'observations');
      }
    } catch (err) {
      const ivError = new InVivoError('Could not save observation', {
        slug: 'observation-view-record-normal',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not save observation');
    } finally {
      recordingNormalRef.current = false;
    }
  }, [recordNormal, observed_at, study.api_id, subject, onSave, workflow.onSave, fetchAnimalDataByType]);

  const orderedObservations = useMemo(
    () =>
      [...observations].sort(
        (firstObservation, secondObservation) =>
          new Date(secondObservation.observed_at).getTime() - new Date(firstObservation.observed_at).getTime()
      ),
    [observations]
  );

  useEffect(() => {
    if (isAddObservationModalOpen || !canWrite) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'BUTTON' ||
          target.tagName === 'A' ||
          target.tagName === 'INPUT' ||
          target.tagName === 'SELECT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();

      if (recordingNormalRef.current) {
        return;
      }

      if (_isNotEmpty(selected)) {
        handleModalToggle();
        return;
      }

      void handleRecordNormalObservation();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selected, handleModalToggle, isAddObservationModalOpen, canWrite, handleRecordNormalObservation]);

  return (
    <div
      className={cn('flex flex-wrap justify-between items-stretch h-100', {
        ui__disabled: !canWrite || loading,
      })}
      data-test-component="ObservationView"
      data-test-element="container"
    >
      <div className="w-50 pa4 br b--moon-gray bg-light-gray" data-test-element="left-pane">
        {_notNil(observationsError) && (
          <Banner critical dismiss={false} className="mw6 mv3">
            <h3 className="f5 normal lh-title pb2">An error has occurred</h3>
            <p className="f6 pb3 lh-copy">
              We are having an issue fetching the team glossary right now, please try again later. If this keeps
              occurring please contact support.
            </p>
            <Button outline critical url={'mailto:support@benchling.com'}>
              Contact support
            </Button>
          </Banner>
        )}
        {loading && <Loading />}
        {_isNotEmpty(observationTypes) && (
          <>
            <div className="ui-card">
              <SearchSelect
                selected={selected}
                setSelected={setSelected}
                sections={observationTypeSections}
                idAccessor="api_id"
              />
            </div>
            <div className="flex flex-column" data-test-element="actions">
              <Button
                className="mt3"
                tooltip={_isEmpty(selected) ? 'Select an observation to continue' : undefined}
                disabled={_isEmpty(selected)}
                onClick={() => handleModalToggle()}
                testId="record-observation-button"
              >
                Record observation{selected.length > 1 && 's'}
              </Button>
              <Button
                className="mt3 plain"
                onClick={handleRecordNormalObservation}
                testId="record-normal-and-save-button"
              >
                Record normal and save
              </Button>
            </div>
          </>
        )}
        {_isEmpty(observationTypes) && (
          <Banner info className="mw6" dismiss={false}>
            <h3 className="f5 normal basier-med lh-title">No team glossary items have been added</h3>
            <p className="f6 pt2 lh-copy">
              To get started, click on Glossary in the left hand menu and add observation glossary items.
            </p>
          </Banner>
        )}
      </div>
      <div className="w-50 bg-white" data-test-element="right-pane">
        <div className="overflow-y-scroll h-100" style={{ maxHeight: '42rem' }}>
          {_isNotEmpty(orderedObservations) ? (
            <ObservationsList study={study} observations={orderedObservations} handleCallback={handleCallback} />
          ) : (
            WorkflowNoDataCard
          )}
        </div>
      </div>
    </div>
  );
};

export default ObservationView;
